import { createSelector } from '@reduxjs/toolkit';

import { getAccountsById } from '../accounts/accountsSelectors';
import type { AccountProps } from '../accounts/accountsTypes';
import { getResourcesById as getBusinessesById, getDefaultBusiness } from '../business/businessSelectors';

export const reportAccountGroups = [
  'Cash & Checking',
  'Savings',
  'Other Banking',
  'Brokerage',
  'Retirement',
  'Education',
  'Other Investments',
  'Real Estate',
  'Vehicle',
  'Other Assets',
  'Credit',
  'Home Loan',
  'Personal Loan',
  'Vehicle Loan',
  'Other Liabilities',
] as const;

export type ReportAccountGroup = (typeof reportAccountGroups)[number];

export const getGroupForAccount = (account: AccountProps): ReportAccountGroup => {
  switch (account.type) {
    case 'CASH':
      return 'Cash & Checking';
    case 'BANK':
      switch (account.subType) {
        case 'DIGITAL_CASH':
        case 'CHECKING':
          return 'Cash & Checking';
        case 'SAVINGS':
        case 'CD':
        case 'MONEY_MARKET':
          return 'Savings';
        default:
          return 'Other Banking';
      }
    case 'INVESTMENT':
      switch (account.subType) {
        case 'BROKERAGE':
          return 'Brokerage';
        case 'IRA':
        case 'KEOGH':
        case 'ROTH_IRA':
        case 'ROTH_401K':
        case 'SEP_IRA':
        case 'SIMPLE_IRA':
        case 'UGMA_UTMA':
        case '401K':
        case '403B':
          return 'Retirement';
        case '529':
          return 'Education';
        default:
          return 'Other Investments';
      }
    case 'REAL_ESTATE':
      return 'Real Estate';
    case 'VEHICLE':
      return 'Vehicle';
    case 'LOAN':
      switch (account.subType) {
        case 'MORTGAGE':
        case 'CONSTRUCTION_LOAN':
        case 'HOME_EQUITY':
          return 'Home Loan';
        case 'AUTO_LOAN':
          return 'Vehicle Loan';
        case 'CONSUMER_LOAN':
        case 'STUDENT_LOAN':
        case 'MILITARY_LOAN':
          return 'Personal Loan';
        case 'LOAN':
        default:
          return 'Other Liabilities';
      }
    case 'CREDIT':
      switch (account.subType) {
        case 'CREDIT_CARD':
        case 'LINE_OF_CREDIT':
          return 'Credit';
        default:
          return 'Other Liabilities';
      }
    case 'ACCOUNTS_PAYABLE':
    case 'OTHER_LIABILITY':
      return 'Other Liabilities';
    case 'OTHER_ASSET':
    case 'PAYMENT':
    case 'ACCOUNTS_RECEIVABLE':
    default:
      return 'Other Assets';
  }
};

export const selectActiveReportAccountGroups = createSelector([getAccountsById], (accounts): ReportAccountGroup[] => {
  const groupCounts = accounts.countBy(getGroupForAccount);
  return reportAccountGroups.filter((group) => (groupCounts.get(group) ?? 0) > 0);
});

export type UsageReportGroup = {
  group: ReportAccountGroup;
  accountIds: string[];
};

export type UsageGroup = {
  key: string;
  label: string;
  createdAt?: string;
  displayThemeBgColor?: string;
  displayThemeColor?: string;
  usageReportGroups: UsageReportGroup[];
  accountIds: string[];
};

const assertUsageGroup = (usageGroup: UsageGroup | undefined, usageKey: string): usageGroup is UsageGroup => {
  assert(usageGroup, `missing usageGroup for usageKey=${usageKey} in selectUsageGroups`);
  return Boolean(usageGroup);
};

export const selectUsageGroups = createSelector(
  [getAccountsById, getBusinessesById, getDefaultBusiness, selectActiveReportAccountGroups],
  (accounts, businesses, defaultBusiness, accountGroups): UsageGroup[] => {
    const byUsage = new Map<string, UsageGroup>();
    accounts.forEach((account) => {
      if (!account.id) return;

      const usageKey = account.businessId || 'PERSONAL';
      if (!byUsage.has(usageKey)) {
        const business = usageKey === 'PERSONAL' ? undefined : businesses.get(usageKey);
        byUsage.set(usageKey, {
          key: usageKey,
          label: usageKey === 'PERSONAL' ? 'Personal' : business?.name || 'Business',
          createdAt: business?.createdAt,
          displayThemeBgColor: business?.color ? `${business.color}.light` : undefined,
          displayThemeColor: business?.color ? `${business.color}.dark` : undefined,
          usageReportGroups: [],
          accountIds: [],
        });
      }

      const usageGroup = byUsage.get(usageKey);
      if (!assertUsageGroup(usageGroup, usageKey)) return;

      usageGroup.accountIds.push(account.id);
      const reportGroup = getGroupForAccount(account);
      const usageReportGroup = usageGroup.usageReportGroups.find((group) => group.group === reportGroup);
      if (usageReportGroup) {
        usageReportGroup.accountIds.push(account.id);
      } else {
        usageGroup.usageReportGroups.push({ accountIds: [account.id], group: reportGroup });
      }
    });

    return Array.from(byUsage.values())
      .sort((a, b) => {
        if (a.key === 'PERSONAL') return 1;
        if (b.key === 'PERSONAL') return -1;
        if (a.key === defaultBusiness?.id) return -1;
        if (b.key === defaultBusiness?.id) return 1;

        const aCreatedAt = a.createdAt ?? '';
        const bCreatedAt = b.createdAt ?? '';
        if (aCreatedAt === bCreatedAt) return 0;
        return aCreatedAt < bCreatedAt ? -1 : 1;
      })
      .map((usageGroup) => ({
        ...usageGroup,
        usageReportGroups: accountGroups
          .map((group) => usageGroup.usageReportGroups.find((usageReportGroup) => usageReportGroup.group === group))
          .filter((group): group is UsageReportGroup => Boolean(group)),
      }));
  },
);
